import { useAutoneTranslation } from '@autone/translations';
import { useCallback, useLayoutEffect, useMemo } from 'react';

import {
  TailwindSelect,
  TailwindSelectContent,
  TailwindSelectItem,
  TailwindSelectTrigger,
  TailwindSelectValue,
} from '../../../../../tailwind';
import { useAdvancedFiltersContext } from '../AdvancedFilters.context';

interface AdvancedFiltersOperatorSelectProps {
  rowId: string;
}

export const AdvancedFiltersOperatorSelect = ({
  rowId,
}: AdvancedFiltersOperatorSelectProps) => {
  const { common } = useAutoneTranslation();
  const { state, dispatch, config } = useAdvancedFiltersContext();

  const { field, operator } = state.conditions[rowId];
  const { operatorOptions, fieldConfigs } = config;
  const isDisabled = !field;

  const currFieldConfig = useMemo(
    () => (field === undefined ? null : fieldConfigs[field]),
    [field, fieldConfigs],
  );

  const validFieldOperators = useMemo(() => {
    if (!currFieldConfig) {
      return [];
    } else {
      return currFieldConfig.operators.map((operator) => ({
        id: operator,
        translatedDisplayName: operatorOptions[operator],
      }));
    }
  }, [operatorOptions, currFieldConfig]);

  const handleUpdateColumnOperator = useCallback(
    (operator: string | undefined) => {
      dispatch({
        type: 'PATCH_CONDITION',
        payload: {
          rowId,
          condition: {
            operator,
          },
        },
      });
    },
    [],
  );

  /**
   * If the current operator is no longer valid for the selected field, clear it.
   */
  useLayoutEffect(() => {
    if (operator === undefined) return;
    else if (currFieldConfig?.operators.includes(operator)) return;

    dispatch({
      type: 'PATCH_CONDITION',
      payload: {
        rowId,
        condition: {
          operator: '',
        },
      },
    });
  }, [currFieldConfig, operator]);

  /**
   * If there's only 1 option, auto-select it.
   */
  useLayoutEffect(() => {
    const numOptions = validFieldOperators.length;
    if (numOptions === 1) {
      handleUpdateColumnOperator(validFieldOperators[0].id);
    }
  }, [validFieldOperators, handleUpdateColumnOperator]);

  return (
    <TailwindSelect
      disabled={isDisabled}
      value={operator}
      onValueChange={handleUpdateColumnOperator}
    >
      <TailwindSelectTrigger>
        <TailwindSelectValue
          placeholder={common('advanced-filters.condition')}
        />
      </TailwindSelectTrigger>
      <TailwindSelectContent data-testid="advanced-filters-operator-select-content">
        {validFieldOperators.map(({ id, translatedDisplayName }) => (
          <TailwindSelectItem key={id} value={id}>
            {translatedDisplayName}
          </TailwindSelectItem>
        ))}
      </TailwindSelectContent>
    </TailwindSelect>
  );
};
